import React, { useRef, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAnglesUp, faAnglesDown, faPlusSquare, faMinusSquare } from "@fortawesome/free-solid-svg-icons";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useLocation, useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import useGlobalParams from "../../hooks/useGlobalParams";
import useSPPosition from "../../hooks/useSPPosition";
import useSPUndo from "../../hooks/useSPUndo";
import "./PositionsSpain.css";
import { ignoreEnter } from "./utils/inputhandlers";
import PositionsSPGeneral from "./PositionsSPGeneral";
import PositionsSPPayment from "./PositionsSPPayment";
import PositionsSPPackaging from "./PositionsSPPackaging";
import PositionsSPInCharge from "./PositionsSPInCharge";
import PositionsSPDelivery from "./PositionsSPDelivery";
import PositionsSPFigures from "./PositionsSPFigures";
import { resetCustoms, resetInbound, resetOceanfreight, resetOutbound } from "./utils/resetFunctions";
import PositionsSPStorage from "./PositionsSPStorage";
import * as Calculations from "./utils/calculations";
import useCalculationsSP from "./hooks/useCalculationsSP";
import { undoLastEdit, redoLastEdit } from "./utils/undo";
import { saveNewSPposition, saveEditsSP } from "./api/APIcalls";
import { gsap } from "gsap";
import ListWithFilters from "../ListWithFilters/ListWithFilters";
import useLoadSPPositions from "./hooks/useLoadSPPositions";
import useLoadSPPositionToEdit from "./hooks/useLoadSPPositionToEdit";

const PositionsSpain = () => {
  // General
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const location = useLocation();

  //Load context values
  const { auth } = useAuth();
  const { globalparams } = useGlobalParams();
  const {
    SPposition,
    setSPposition,
    SPpositionData,
    setSPpositionData,
    SPpositionInit,
    SPpositionDataInit,
    positiontoeditSP,
    setPositiontoeditSP,
    SPpaytermInit,
    SPpaytermDataInit,
    SPpaymentterm,
    SPpaymenttermData,
    setSPPaymentterm,
    setSPPaymenttermData,
    SPpayrow,
  } = useSPPosition();
  const {
    positionEditsSP,
    editIDSP,
    setEditIDSP,
    undoListSP,
    showcalculatorSP,
    setShowcalculatorSP,
    undoFocusFlagSP,
    setUndoFocusFlagSP,
    inputRefsSP,
  } = useSPUndo();

  // Refs
  const pSPsearchRef = useRef();
  const SPMRef = useRef();
  const loadedPaytermSP = useRef(structuredClone(SPpaytermInit));
  const loadedPaytermDataSP = useRef(structuredClone(SPpaytermDataInit));
  const loadedPosition = useRef(SPposition);
  const loadedPositionData = useRef(SPpositionData);

  // States
  const [toggleClearSF, setToggleClearSF] = useState(false); // flag to clear searchfields
  const [updateLoadedPaytermsSP, setUpdatedLoadedPaytermsSP] = useState(false);
  const [loaded, setLoaded] = useState(false); // flag to trigger payterms modification in useeffect after loading
  const [showinfo, setShowinfo] = useState(); // flag to show incoterm info
  const [reloadlistdata, setReloadlistdata] = useState(); // flag to reload position list data
  const [clearSelectedRowID, setClearSelectedRowID] = useState(); // clears selected row in positions list
  const [SPM, setSPM] = useState(); // holds notification msg
  const [showSPM, setShowSPM] = useState(false); // flag to show notification msg
  const [showPSPSearch, setShowPSPSearch] = useState(false); // flat to show positions list component

  // Use Effects

  // Store loaded position to compare edits against
  useEffect(() => {
    loadedPosition.current = SPposition;
    loadedPositionData.current = SPpositionData;
  }, [loaded]);

  // Update paymentterm and paymenttermdata after loading to include a row value
  useEffect(() => {
    setSPPaymentterm((prev) =>
      prev.map((item) => {
        return { ...item, ["row"]: item.posPaytermID || SPpayrow.current };
      })
    );
    setSPPaymenttermData((prev) =>
      prev.map((item) => {
        return { ...item, ["row"]: item.posPaytermID || SPpayrow.current };
      })
    );
    return setUpdatedLoadedPaytermsSP((prev) => !prev);
  }, [loaded]);

  // Store loaded payment terms to compare edits against. Do this after update above.
  useEffect(() => {
    loadedPaytermSP.current = structuredClone(SPpaymentterm);
    loadedPaytermDataSP.current = structuredClone(SPpaymenttermData);
    SPpayrow.current = 0;
  }, [updateLoadedPaytermsSP]);

  // Clear Position Function
  const clearSPPosition = () => {
    setSPposition(SPpositionInit);
    setSPpositionData(SPpositionDataInit);
    setSPPaymentterm(SPpaytermInit);
    setSPPaymenttermData(SPpaytermDataInit);
    setPositiontoeditSP(null);
    setClearSelectedRowID(false);
    setLoaded((prev) => !prev);
    setUpdatedLoadedPaytermsSP((prev) => !prev);
    setToggleClearSF((prev) => !toggleClearSF);
    setEditIDSP(0);
  };

  // Set Insurance Variables when Incoterm changes
  useEffect(() => {
    SPposition &&
      Calculations.setInsuranceVars(
        SPposition.sincoterm,
        globalparams,
        setSPposition,
        setSPpositionData,
        positionEditsSP,
        editIDSP
      );
  }, [SPposition.sincoterm]);

  //Calculations Hook
  useCalculationsSP(SPpositionData, SPpaymenttermData);

  // Load List Data
  const listdata = useLoadSPPositions(axiosPrivate, reloadlistdata, navigate, location, "loadSPpositions");

  // Show/Hide Position List Component
  useEffect(() => {
    if (showPSPSearch) {
      const elem = pSPsearchRef.current;
      gsap.to(elem, {
        bottom: "-1vh",
        duration: 0.1,
        ease: "power1.inOut",
      });
    }
    if (!showPSPSearch) {
      const elem = pSPsearchRef.current;
      gsap.to(elem, {
        bottom: "-73vh",
        duration: 0.1,
        ease: "power1.inOut",
      });
    }
  }, [showPSPSearch]);

  // Give focus to the undo input field using inputRefs
  useEffect(() => {
    if (undoFocusFlagSP && inputRefsSP.current[undoFocusFlagSP]) {
      inputRefsSP.current[undoFocusFlagSP].focus();
      setToggleClearSF((prev) => !prev);
    }
    return setUndoFocusFlagSP("");
  }, [undoFocusFlagSP]);

  // Attach Undo and Redo functions with addEventListener
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for MetaKey+Z (Undo)
      if ((e.metaKey || e.ctrlKey) && e.key === "z" && !e.shiftKey) {
        undoLastEdit(
          e,
          setToggleClearSF,
          positionEditsSP,
          undoListSP,
          setSPposition,
          setSPpositionData,
          setShowcalculatorSP,
          setUndoFocusFlagSP,
          loadedPosition,
          loadedPositionData,
          setEditIDSP,
          setSPPaymentterm,
          setSPPaymenttermData,
          SPpaymentterm,
          SPpaymenttermData,
          loadedPaytermSP,
          loadedPaytermDataSP
        );
      }
      // Check for MetaKey+Shift+Z (Redo)
      if ((e.metaKey || e.ctrlKey) && e.key === "z" && e.shiftKey) {
        redoLastEdit(
          e,
          setToggleClearSF,
          undoListSP,
          positionEditsSP,
          setSPposition,
          setSPpositionData,
          setShowcalculatorSP,
          setUndoFocusFlagSP,
          setEditIDSP,
          setSPPaymentterm,
          setSPPaymenttermData,
          loadedPaytermSP,
          loadedPaytermDataSP
        );
      }
    };
    // Add the event listener
    window.addEventListener("keydown", handleKeyDown);
    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Show Saved Position Message
  const onSPMComplete = () => {
    setSPM(null);
  };
  useEffect(() => {
    const elem = SPMRef.current;
    if (SPM) {
      gsap.fromTo(elem, { opacity: 0.5, top: "-50px" }, { opacity: 1, top: 0, duration: 0.1, ease: "power2.in" });
      gsap.fromTo(
        elem,
        { opacity: 1 },
        {
          opacity: 0,
          duration: 3,
          ease: "power2.inOut",
          onComplete: onSPMComplete,
        }
      );
    }
  }, [showSPM]);

  // Select Position to Edit
  const selectPositionToEdit = (item) => {
    clearSPPosition();
    setPositiontoeditSP(item.SPposID);
    setShowPSPSearch(false);
  };

  // Load Position when Navigating from USQS
  useEffect(() => {
    console.log(location.state?.positiontoedit);
    if (location.state?.positiontoedit) {
      setPositiontoeditSP(location.state.positiontoedit);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Load Position to Edit
  useLoadSPPositionToEdit(
    axiosPrivate,
    positiontoeditSP,
    setSPposition,
    setSPpositionData,
    setSPPaymentterm,
    setSPPaymenttermData,
    SPpaytermInit,
    SPpaytermDataInit,
    setLoaded,
    navigate,
    location
  );

  return (
    <div className='addposSP-container'>
      <form
        className='addpositionSP'
        onSubmit={(e) => {
          e.preventDefault();
          if (positiontoeditSP) {
            saveEditsSP(
              positionEditsSP,
              axiosPrivate,
              positiontoeditSP,
              setReloadlistdata,
              setSPM,
              setShowSPM,
              SPpaymenttermData,
              SPposition.priceafterintins
            );
          } else {
            console.log("submitting");
            saveNewSPposition(
              axiosPrivate,
              SPpositionData,
              SPpaymenttermData,
              setReloadlistdata,
              reloadlistdata,
              setSPM,
              setShowSPM,
              showSPM,
              clearSPPosition
            );
            // clearSPPosition();
          }
        }}
        onClick={(e) => setShowPSPSearch(false)}
      >
        <div className='addposSP-header'>
          <h1 style={{ marginLeft: "1rem" }}>
            {positiontoeditSP ? "Edit Spain Position:" : "Add New Spain Position:"}
          </h1>
          <div className='posUS-success-msg' ref={SPMRef}>
            {SPM}
          </div>
          <div className='form-buttons'>
            <button
              type='button'
              tabIndex={201}
              onClick={(e) => {
                clearSPPosition();
              }}
            >
              Clear
            </button>
            {!positiontoeditSP && (
              <button className='mainbutton' type='submit' tabIndex={201}>
                Save New
              </button>
            )}
            {positiontoeditSP && (
              <button
                // disabled={editing}
                className={positionEditsSP.current.length > 0 ? "mainbutton" : "mainbutton btndisabled"}
                type='submit'
                tabIndex={201}
              >
                Save Edits
              </button>
            )}
          </div>
        </div>
        <div className='addposSP-formcontainer'>
          <div className='addposSP-1'>
            <PositionsSPGeneral
              ignoreEnter={(e) => ignoreEnter(e, setShowcalculatorSP, showcalculatorSP)}
              clearfield={toggleClearSF}
            />
            <PositionsSPPayment
              loadedPayterm={loadedPaytermSP}
              loadedPaytermData={loadedPaytermDataSP}
              ignoreEnter={(e) => ignoreEnter(e, setShowcalculatorSP, showcalculatorSP)}
              clearfield={toggleClearSF}
            />
            <PositionsSPPackaging clearfield={toggleClearSF} />
          </div>
          <div className='addposSP-2' style={{ overflow: "visible" }}>
            <PositionsSPInCharge auth={auth} clearfield={toggleClearSF} />
            <PositionsSPDelivery setShowinfo={setShowinfo} showinfo={showinfo} clearfield={toggleClearSF} />
          </div>
          <div className='addposSP-3'>
            <div className='addposSP-31'>
              <PositionsSPFigures
                ignoreEnter={(e) => ignoreEnter(e, setShowcalculatorSP, showcalculatorSP)}
                setShowcalculator={setShowcalculatorSP}
                showcalculator={showcalculatorSP}
                resetOutbound={(e) =>
                  resetOutbound(
                    SPposition,
                    SPpositionData,
                    setSPposition,
                    setSPpositionData,
                    SPpositionInit,
                    SPpositionDataInit,
                    positionEditsSP,
                    editIDSP,
                    setEditIDSP
                  )
                }
                resetOceanfreight={(e) =>
                  resetOceanfreight(setSPposition, setSPpositionData, SPpositionInit, SPpositionDataInit)
                }
                resetCustoms={(e) => resetCustoms(setSPposition, setSPpositionData, SPpositionInit, SPpositionDataInit)}
                resetInbound={(e) => resetInbound(setSPposition, setSPpositionData, SPpositionInit, SPpositionDataInit)}
                clearfield={toggleClearSF}
              />
            </div>
            <div className='addposSP-32'>
              <PositionsSPStorage clearfield={toggleClearSF} ignoreEnter={(e) => ignoreEnter(e)} />
            </div>
          </div>
        </div>
      </form>
      <div className='possearchSP' ref={pSPsearchRef} tabIndex={-1}>
        <div className='possearchSP-header' onClick={(e) => setShowPSPSearch(!showPSPSearch)}>
          {!showPSPSearch ? <FontAwesomeIcon icon={faAnglesUp} /> : <FontAwesomeIcon icon={faAnglesDown} />}
        </div>
        <div style={{ marginTop: "1rem" }}>
          <ListWithFilters
            data={listdata}
            columns={[
              "posDate",
              "SPposID",
              "WGP",
              "abbreviation",
              "supplier",
              "shipfrom",
              "shipuntil",
              "portOfLoad",
              "portOfDestination",
              "quantity",
            ]}
            size={["S", "S", "S", "M", "M", "S", "S", "M", "M", "S", "S"]}
            rowID={"SPposID"}
            placeHolders={true}
            placeHolderList={[
              "QSDate",
              "SPposID",
              "WGP",
              "Product",
              "Supplier",
              "From",
              "To",
              "POL",
              "POD",
              "Quantity",
            ]}
            tabIndex={-1}
            onRowClick={selectPositionToEdit}
            setClearSelectedRowID={setClearSelectedRowID}
            clearSelectedRowID={clearSelectedRowID}
          />
        </div>
      </div>
    </div>
  );
};

export default PositionsSpain;
